// https://tiptap.dev/docs/editor/core-concepts/introduction
import { EditorContent, useEditor } from '@tiptap/react';
import { ComponentType, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { LoadingAddon } from '../../primitives/loading-addon';
import { Separator } from '../../primitives/separator';
import { TooltipProvider } from '../../primitives/tooltip';

import { BubbleMenu } from './menus/bubble-menu';
import { FixedMenu } from './menus/fixed-menu';

import { type MediaPickerComponent } from './commands/button-image';
import { CharacterCount } from './components/character-count';

import { cn } from '../../utils/cn';
import { type EditorControl } from './controls';
import { extensions, ExtensionsProps } from './extensions';
import { parseContentFromSchema } from './utils/parse-content-from-schema';
import { serializeHtmlToMarkdown } from './utils/serialize-html-to-markdown';

import 'prosemirror-view/style/prosemirror.css';
import './styles.css';
import styles from './styles.module.css';

export { EDITOR_CONTROLS } from './controls';
export type { EditorControl } from './controls';

export type EditorContent = {
  html: string;
  isEmpty: boolean;
  markdown: string;
};

export type EditorRef = {
  getContent: () => EditorContent;
};

interface EditorProps {
  content?: string;
  controls?: EditorControl[];
  disabled?: boolean;
  ext?: ExtensionsProps;
  id?: string;
  isLoading?: boolean;
  mediaPicker?: ComponentType<MediaPickerComponent>;
  onChange?: (content: EditorContent) => void;
}

/**
 * Editor
 * @param content **optional** Initializes with rich text or update all content.
 * @param disabled **optional** enables or disables the entire editor.
 * @callback `onChange` Optional callback emitted when the editor content changes.
 * @callback `getContent` Available through `ref`.
 */
export const Editor = forwardRef<EditorRef, EditorProps>(function Editor(
  { content, controls, disabled = false, ext, id, isLoading = false, mediaPicker, onChange },
  ref,
) {
  const isSyncingContentRef = useRef(true);
  const editor = useEditor({
    editorProps: {
      attributes: {
        class: cn(styles['editor-content'], {
          'opacity-50': disabled || isLoading,
        }),
        id: id ?? '',
      },
    },
    extensions: extensions({ mention: ext?.mention }),
    injectCSS: true,
    onUpdate: ({ editor }) => {
      if (isSyncingContentRef.current) return;
      onChange?.({
        html: editor.getHTML(),
        isEmpty: editor.isEmpty,
        markdown: serializeHtmlToMarkdown.serialize(editor.state.doc),
      });
    },
  });

  useEffect(() => {
    if (!editor) return;
    if ((content ?? '') === serializeHtmlToMarkdown.serialize(editor.state.doc)) {
      isSyncingContentRef.current = false;
      return;
    }

    isSyncingContentRef.current = true;
    const frameId = requestAnimationFrame(() => {
      if (!editor.isDestroyed) {
        editor.commands.setContent(parseContentFromSchema({ content, editor }), {
          emitUpdate: false,
        });
      }
      isSyncingContentRef.current = false;
    });
    return () => cancelAnimationFrame(frameId);
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useImperativeHandle(ref, () => ({
    getContent() {
      if (!editor) {
        return { html: '', isEmpty: true, markdown: '' };
      }
      return {
        html: editor.getHTML(),
        isEmpty: editor.isEmpty,
        markdown: serializeHtmlToMarkdown.serialize(editor.state.doc),
      };
    },
  }));

  if (!editor) return null;

  return (
    <div
      className={cn('relative rounded-lg border border-border shadow-xs', {
        'cursor-not-allowed border-input/50': disabled || isLoading,
      })}
    >
      <TooltipProvider>
        <FixedMenu controls={controls} editor={editor} mediaPicker={mediaPicker} />
        <BubbleMenu controls={controls} editor={editor} />
      </TooltipProvider>
      <Separator className={cn({ 'opacity-50': disabled || isLoading })} />
      {/* `div` added to solve tippy. Ref: https://atomiks.github.io/tippyjs/v6/accessibility/#interactivity */}
      <div>
        <EditorContent editor={editor} />
      </div>
      <CharacterCount editor={editor} hasEmoji hasWords />
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
});
